import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ListCardBone, SkeletonLoader, useModal } from '@europrocurement/flexy-components';
import {
    BillingContext,
    DocumentPreviewContext,
    DossierFacturation,
    DossierPrescriberContext,
    InvoiceItemProps,
    InvoiceList,
    InvoiceStatus,
    ModalPaiement,
    NoResult,
    SelectedDossierContext,
    UpdateCollectionPayload,
    useBilling,
    useCollectionDossier,
    useDossier,
    useDossierCollection,
    useDossierPrescripteur,
    useDownload,
    useExportDossier,
    useModalMail,
    usePanierPay,
    useToaster,
} from '@europrocurement/l2d-modules';
import {
    FactureFormaliteApiObject,
    useFacturationService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import useDossierPrefacturation, {
    DossiersArticlesReductions,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierPrefacturation';
import useCollectionProvision, {
    CollectionProvisionType,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useCollectionProvision';
import { CollectionDeDossierJsonldCollectiondossierRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import useCollectionReglement from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useCollectionReglement';
import {
    AjustementDePrestationFormalitJsonldAjustementCreate,
    ArticleTarifDTOJsonldDonneesFacturationRead,
    DossierTarifDTOJsonldDonneesFacturationRead,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { useParams } from 'react-router';
import BillingAddressSection from './components/BillingAddressSection/BillingAddressSection';
import QuoteAndBillsSection from './components/QuoteAndBillsSection/QuoteAndBillsSection';

type FacturationTabProps = {
    initialCollectionsIds?: number[];
};
const FacturationTab: React.FunctionComponent<FacturationTabProps> = function ({
    initialCollectionsIds,
}) {
    const { dossier } = useContext(SelectedDossierContext);

    const { setDocumentPreview } = useContext(DocumentPreviewContext);

    const { idsOrders } = useParams();
    const collectionsIds = useMemo(
        () => (initialCollectionsIds || (idsOrders ? JSON.parse(idsOrders) : [])) as number[],
        [initialCollectionsIds, idsOrders],
    );

    const [currentCollectionsIds, setCurrentCllectionsIds] = React.useState<Array<number>>([]);

    const [collections, setCollections] =
        useState<CollectionDeDossierJsonldCollectiondossierRead[]>();
    const { getBillingAddress } = useDossierPrescripteur();

    const [forcedReload, setForcedReload] = useState<boolean>();
    const [clientId, setClientId] = useState<number>();
    const [defaultIndirectSale, setDefaultIndirectSale] = useState<boolean>();
    const [clientOpereId, setClientOpereId] = useState<number>();

    const [prescripteurId, setPrescripteurId] = useState<number>();
    const [recipientId, setRecipientId] = useState<number>();

    const [dataPrefacturation, setDataPrefacturation] = useState<DossiersArticlesReductions | null>(
        null,
    );
    const [acomptes, setAcomptes] = useState<Array<CollectionProvisionType>>([]);
    const [reglements, setReglements] = useState<Array<CollectionProvisionType>>([]);
    const [totalRemainingAmount, setTotalRemainingAmount] = useState<number>(0);
    const [invoiceToPayItems, setInvoiceToPayItems] = useState<Array<number>>([]);
    const { getInvoiceRemainingAmount } = useBilling({});
    const { searchDossierArticlesEtReductions, getDossiersFacturables } =
        useDossierPrefacturation();
    const { ajustementDePrestationFormalitApi } = useFacturationService();
    const { updateDossierArticles } = useDossier();
    const { searchCollectionProvision } = useCollectionProvision();
    const { searchCollectionReglement } = useCollectionReglement();

    const { handleApiResponse } = useToaster();
    const { modalActions } = useModal();
    const { getCollections } = useDossierCollection();
    const { updateCollections } = useCollectionDossier();
    const xIdSociete = useSociete();

    const [actif, setActif] = useState<boolean | undefined>(true);
    const origine = 'B2D';
    const {
        estimates,
        invoices,
        estimatesLoaded,
        invoicesLoaded,
        setReload,
        updateActif,
        setCollectionsIds,
    } = useContext(BillingContext);
    const [invoiceItems, setInvoiceItems] = useState<Array<InvoiceItemProps>>([]);

    const { prescriber, sousClients, prescriberLoaded, setPrescriberId } =
        useContext(DossierPrescriberContext);

    const [dossiersIds, setDossiersIds] = React.useState<Array<number>>([]);
    const [dossiersSelected, setDossiersSelected] = React.useState<Array<number>>([]);

    const [indirectSale, setIndirectSale] = useState<boolean>();
    const [actualDossiersSelected, setActualDossiersSelected] = React.useState<
        Array<number> | undefined
    >();

    const [onload, setOnload] = React.useState<boolean>(false);

    const { enqueueSnackbar } = useSnackbar();
    useEffect(() => {
        if (
            JSON.stringify(currentCollectionsIds) !== JSON.stringify(collectionsIds) ||
            forcedReload
        ) {
            setForcedReload(false);
            setCurrentCllectionsIds(collectionsIds);
            setCollectionsIds(collectionsIds);
            getCollections(collectionsIds).then((data) => {
                if (!data[0]) {
                    enqueueSnackbar('Erreur : aucune commande.', {
                        variant: 'error',
                    });
                    throw new Error('aucune collection');
                }

                const clientIds = data.map((collection) => collection.client);
                const uniqueClientIds = new Set(clientIds);
                if (uniqueClientIds.size > 1) {
                    enqueueSnackbar(
                        'Erreur : les commandes doivent être facturées au même client.',
                        {
                            variant: 'error',
                        },
                    );
                    throw new Error('collections avec des clients différents');
                }
                if (!data[0].prescripteur) {
                    enqueueSnackbar('Erreur : les commandes doivent avoir un prescripteur.', {
                        variant: 'error',
                    });
                    throw new Error('collection sans prescripteur');
                }
                if (!data[0].client) {
                    enqueueSnackbar('Erreur : les commandes doivent avoir un client.', {
                        variant: 'error',
                    });
                    throw new Error('collection sans client');
                }

                setRecipientId(data[0].client.replace('/tiers/tiers/', '') as unknown as number);

                setClientId(data[0].client.replace('/tiers/tiers/', '') as unknown as number);
                const ids = data.flatMap((collection) => {
                    if (!collection.dossiers) {
                        throw new Error('collection sans dossier');
                    }
                    return collection.dossiers.map((dossierCollection) => {
                        if (!dossierCollection.id) {
                            throw new Error('dossier sans id');
                        }
                        return dossierCollection.id as number;
                    });
                });
                setPrescriberId(
                    data[0].prescripteur.replace('/tiers/tiers/', '') as unknown as number,
                );
                setPrescripteurId(
                    data[0].prescripteur.replace('/tiers/tiers/', '') as unknown as number,
                );

                setDefaultIndirectSale(data?.[0].venteIndirecte);
                setIndirectSale(data?.[0].venteIndirecte);
                setClientOpereId(data?.[0]?.dossiers?.[0]?.clientOpere as number | undefined);
                setDossiersIds(ids);
                getDossiersFacturables(ids)
                    .then((fetchedSelected) => {
                        setDossiersSelected(fetchedSelected);
                    })
                    .catch((error) => {
                        console.error(
                            'Erreur lors du chargement des dossiers facturables / prefacturation',
                            error,
                        );
                    });
                searchCollectionProvision(collectionsIds).then(setAcomptes);
                searchCollectionReglement(collectionsIds).then(setReglements);
                setCollections(data);
            });
        }
    }, [
        forcedReload,
        collections,
        collectionsIds,
        currentCollectionsIds,
        enqueueSnackbar,
        getCollections,
        getDossiersFacturables,
        searchCollectionProvision,
        searchCollectionReglement,
        setCollectionsIds,
        setPrescriberId,
    ]);

    const clientAddresses = sousClients.map((client) => ({
        clientId: client.id as number,
        label: client.raisonSociale,
        siren: client.siren as string,
        address: getBillingAddress(client),
    }));

    const {
        getEstimateLabel,
        getInvoiceLabel,
        isInvoicePaid,
        downloadInvoice,
        downloadEstimate,
        canUpdateBillingInfos,
        validateInvoice,
        deleteInvoice,
        avoirInvoice,
    } = useBilling({ origine });
    const { download } = useDownload();

    const handleRecipientChange = (newRecipientId?: number) => {
        setRecipientId(
            newRecipientId ||
                (prescripteurId !== clientOpereId ? clientOpereId : (sousClients[0].id as number)),
        );

        // Add notification to user.
        enqueueSnackbar('Le client facturé a bien été modifié.', {
            variant: 'success',
        });
    };

    const handleIndirectSaleChange = (newIndirectSale: boolean) => {
        setIndirectSale(newIndirectSale);

        // Add notification to user.
        enqueueSnackbar('Le choix du recouvrement a bien été modifié.', {
            variant: 'success',
        });
    };

    const statusFromInvoice = (invoice: FactureFormaliteApiObject): InvoiceStatus => {
        if (invoice.factureEnPreparation) {
            return 'draft';
        }
        if (isInvoicePaid(invoice)) {
            return 'paid';
        }

        return 'pending';
    };

    const feedbackFromInvoice = (invoice: FactureFormaliteApiObject): string => {
        if (invoice.factureEnPreparation) {
            return 'En préparation';
        }
        if (isInvoicePaid(invoice)) {
            return 'Réglé';
        }

        return 'À régler';
    };

    useEffect(() => {
        updateActif(actif);
    }, [actif, updateActif]);

    useEffect(() => {
        const payload: UpdateCollectionPayload = {
            clientId: undefined,
            venteIndirecte: undefined,
        };

        if (clientId !== recipientId) {
            payload.clientId = recipientId;
        }

        if (prescripteurId === recipientId) {
            payload.venteIndirecte = false;
        } else {
            const sousClient = sousClients.find((t) => t.id === recipientId);

            if (sousClient) {
                payload.venteIndirecte = sousClient.venteIndirecte;
            }
        }

        const newIndirectSale = payload.venteIndirecte || defaultIndirectSale;
        const newClientId = payload.clientId || clientId;
        const hasChanges = newIndirectSale !== indirectSale || newClientId !== clientId;

        if (!hasChanges) {
            return;
        }

        if (newIndirectSale !== indirectSale) {
            setIndirectSale(newIndirectSale);
        }

        updateCollections(collectionsIds, {
            clientId: payload.clientId || clientId,
            venteIndirecte: newIndirectSale,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipientId]);

    useEffect(() => {
        if (!collectionsIds) {
            enqueueSnackbar('Aucune commande liée au dossier sélectionné', {
                variant: 'error',
            });
            return;
        }
        if (defaultIndirectSale !== indirectSale) {
            setDefaultIndirectSale(indirectSale);
            updateCollections(collectionsIds, { venteIndirecte: indirectSale });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indirectSale]);

    const allDataLoaded = prescriberLoaded;

    const reloadInvoiceEstimates = () => {
        setReload(true);
    };

    const reloadDossierArticlesEtReductions = (ids: number[], idsSelected: number[]) => {
        setOnload(true);
        searchDossierArticlesEtReductions(ids, idsSelected)
            .then(setDataPrefacturation)
            .finally(() => {
                setOnload(false);
            })
            .catch(() => {
                setOnload(false);
            });
    };

    const reloadAllData = () => {
        reloadInvoiceEstimates();
        if (dossiersIds && dossiersIds.length > 1) {
            getDossiersFacturables(dossiersIds)
                .then((fetchedSelected) => {
                    setDossiersSelected(fetchedSelected);

                    reloadDossierArticlesEtReductions(dossiersIds, fetchedSelected);
                })
                .catch((error) => {
                    console.error(
                        'Erreur lors du chargement des dossiers facturables / prefacturation',
                        error,
                    );
                });
        } else {
            reloadDossierArticlesEtReductions(dossiersIds, dossiersSelected);
        }
        searchCollectionProvision(collectionsIds).then(setAcomptes);
        searchCollectionReglement(collectionsIds).then(setReglements);
        getCollections(dossiersIds).then(setCollections);
    };

    const handleDownloadInvoice = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadInvoice(id)
                .then((fileContent: Blob) => {
                    download(fileName, fileContent);

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleShowInvoice = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadInvoice(id)
                .then((fileContent: Blob) => {
                    setDocumentPreview({
                        fileName,
                        fileType: 'pdf',
                        content: fileContent,
                    });

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleDownloadEstimate = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadEstimate(id)
                .then((fileContent: Blob) => {
                    download(fileName, fileContent);

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleShowEstimate = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadEstimate(id)
                .then((fileContent: Blob) => {
                    setDocumentPreview({
                        fileName,
                        fileType: 'pdf',
                        content: fileContent,
                    });

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleError = (error: AxiosError | Error) => {
        console.log(error);
        if (
            error instanceof AxiosError &&
            error.response &&
            error.response.data.exception &&
            error.response.data.exception.message
        ) {
            enqueueSnackbar(error.response.data.exception.message, {
                variant: 'error',
            });
        } else {
            enqueueSnackbar(
                error instanceof Error
                    ? error.message
                    : 'Une erreur est survenue lors de la validation de la facture',
                {
                    variant: 'error',
                    autoHideDuration: 2000,
                },
            );
        }
    };
    const handleValidateInvoice = (invoice: FactureFormaliteApiObject): Promise<void> =>
        new Promise(() => {
            if (!invoice.id) {
                throw new Error('Invoice ID is required');
            }
            validateInvoice(invoice.id)
                .then(() => {
                    enqueueSnackbar('Facture validée', {
                        autoHideDuration: 2000,
                    });
                    reloadAllData();
                })
                .catch((error) => {
                    handleError(error);
                });
        });

    const handleDeleteInvoice = (invoice: FactureFormaliteApiObject): Promise<void> =>
        new Promise(() => {
            if (!invoice.id) {
                throw new Error('Invoice ID is required');
            }
            if (invoice.factureEnPreparation) {
                deleteInvoice(invoice.id)
                    .then(() => {
                        enqueueSnackbar('Facture supprimée', {
                            autoHideDuration: 2000,
                        });
                        reloadAllData();
                    })
                    .catch((error) => {
                        handleError(error);
                    });
            } else {
                avoirInvoice(invoice.id)
                    .then(() => {
                        enqueueSnackbar('Avoir créé', {
                            autoHideDuration: 2000,
                        });
                        reloadAllData();
                    })
                    .catch((error) => {
                        handleError(error);
                    });
            }
        });

    useEffect(() => {
        setInvoiceToPayItems([
            ...invoices
                .filter((item) => item.paye === false && item.factureEnPreparation === false)
                .map((estimate) => estimate.id as number),
        ]);

        setInvoiceItems([
            ...invoices.map((invoice) => ({
                label: getInvoiceLabel(invoice),
                status: statusFromInvoice(invoice),
                validateInvoice: invoice.factureEnPreparation
                    ? () => {
                          handleValidateInvoice(invoice);
                      }
                    : null,
                deleteInvoice:
                    invoice.id !== undefined &&
                    invoice.idFactureRefAvoir === null &&
                    invoice.totalTtc !== null &&
                    invoice.totalTtc !== undefined &&
                    Number(invoice.totalTtc) > 0
                        ? () => {
                              handleDeleteInvoice(invoice);
                          }
                        : null,
                feedback: feedbackFromInvoice(invoice),
                id: invoice.id as number,
                onDownload: handleDownloadInvoice,
                onShow: handleShowInvoice,
            })),
            ...estimates.map((estimate) => ({
                label: getEstimateLabel(estimate),
                status: 'paid' as InvoiceStatus,
                id: estimate.id as number,
                onDownload: handleDownloadEstimate,
                onShow: handleShowEstimate,
            })),
        ]);
        setTotalRemainingAmount(
            invoices
                .filter((item) => item.paye === false && item.factureEnPreparation === false)
                .reduce((prev, current) => prev + getInvoiceRemainingAmount(current), 0),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estimates, invoices]);

    let devisFactures;

    if (!estimatesLoaded || !invoicesLoaded) {
        devisFactures = undefined;
    } else if (invoiceItems.length > 0) {
        devisFactures = <InvoiceList items={invoiceItems} />;
    } else {
        devisFactures = (
            <NoResult
                title="Aucun devis ou facture pour le moment"
                paddingSx="15px"
            />
        );
    }

    useEffect(() => {
        if (actualDossiersSelected !== dossiersSelected) {
            setActualDossiersSelected(dossiersSelected);
            if (actualDossiersSelected !== undefined) {
                reloadDossierArticlesEtReductions(dossiersIds, dossiersSelected);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossiersSelected]);

    const afterSubmit = () => {
        modalActions.close();
        if (dossiersIds && dataPrefacturation) {
            if (dataPrefacturation.dossiers?.length === 1) {
                // test de la facturabilité du dossier et selection automatique du dossier si facturable
                getDossiersFacturables(dossiersIds)
                    .then((fetchedSelected) => {
                        setDossiersSelected(fetchedSelected);
                    })
                    .catch((error) => {
                        console.error(
                            'Erreur lors du chargement des dossiers facturables / prefacturation',
                            error,
                        );
                    });
            } else {
                // mise à jour des dossiers facturables et reload des dossiers
                reloadDossierArticlesEtReductions(dossiersIds, dossiersSelected);
            }
        }
    };

    const afterSubmitAcompte = () => {
        modalActions.close();
        searchCollectionProvision(collectionsIds).then(setAcomptes);
    };
    const afterSubmitInvoice = () => {
        reloadAllData();
    };
    const afterSubmitAcompteAttendu = () => {
        modalActions.close();
        setForcedReload(true);
        getCollections(dossiersIds).then(setCollections);
    };
    const sortableListOnChange = (
        items: (Omit<ArticleTarifDTOJsonldDonneesFacturationRead, 'id'> & {
            id: number;
        })[],
        articleDossier: DossierTarifDTOJsonldDonneesFacturationRead,
        dossierIdx: number,
    ) => {
        if (!dataPrefacturation) {
            throw new Error('dataPrefacturation is null');
        }
        // changer le rang de tous les items
        const newItems = items.map((item, index) => ({
            ...item,
            rang: index * 10 + 10,
        }));
        // constuire un nouveau dossier
        const newDossiers = [...(dataPrefacturation.dossiers || [])];

        if (!dataPrefacturation.dossiers || !dataPrefacturation.dossiers[dossierIdx]) {
            throw new Error('Prefacturation sans dossiers');
        }

        newDossiers.splice(dossierIdx, 1, {
            ...dataPrefacturation.dossiers[dossierIdx],
            articles: newItems,
        });

        setDataPrefacturation({
            ...dataPrefacturation,
            dossiers: newDossiers,
        });

        if (!articleDossier.id) {
            throw new Error('dossier sans id');
        }
        if (
            newDossiers[dossierIdx].articles === undefined ||
            newDossiers[dossierIdx].articles.length === 0
        ) {
            throw new Error('dossier sans articles');
        }

        const articlesUpdateDossierArticles = newDossiers[dossierIdx].articles.map((article) => ({
            id: article.id,
            rang: article.rang,
            libelle: article.libelle !== undefined ? article.libelle : null,
        }));

        updateDossierArticles(articleDossier.id, articlesUpdateDossierArticles);
    };

    const ajustInvoice = async () => {
        const articles =
            dataPrefacturation?.dossiers &&
            dataPrefacturation.dossiers.flatMap(
                (prefacturationdossier) =>
                    prefacturationdossier.articles?.map((article) => article.id as number) || [],
            );

        const acompteIds: number[] = acomptes.flatMap((acompte) =>
            (acompte.detailsReglements ?? []).map((detail) => {
                if (detail.id === undefined || detail.id === null) {
                    throw new Error('detail sans id');
                }
                return detail.id;
            }),
        );

        const ajustementDePrestationFormalitJsonldAjustementCreate: AjustementDePrestationFormalitJsonldAjustementCreate =
            {
                articles,
                acomptes: acompteIds,
            };
        const ajustementRequest =
            ajustementDePrestationFormalitApi.apiAjustementArticlesPrestationPost({
                xIdSociete,
                ajustementDePrestationFormalitJsonldAjustementCreate,
            });
        await handleApiResponse(ajustementRequest, afterSubmit);
    };

    const { bodyMailCollection, bodyMailFacture } = usePanierPay();
    const { showMailModal } = useModalMail();
    const { searchContacts } = useExportDossier();

    const onClickDemandeReglement = useCallback(
        async (type: string) => {
            try {
                if (!recipientId) {
                    enqueueSnackbar('Aucun client sélectionné', {
                        variant: 'error',
                    });
                    return;
                }
                if (!clientId) {
                    enqueueSnackbar('Aucun client sélectionné', {
                        variant: 'error',
                    });
                    return;
                }
                if (!prescripteurId) {
                    enqueueSnackbar('Aucun prescripteur sélectionné', {
                        variant: 'error',
                    });
                    return;
                }
                if (!collections) {
                    enqueueSnackbar('Aucune commande sélectionnée', {
                        variant: 'error',
                    });
                    return;
                }

                // Dans le cas d'un dossier NOR ou DOD on sélectionne par défaut le contact compta du prescripteur.
                // Dans le cas d'un dossier DOI on sélectionne le contact du client facturé.
                const tiersAContacter = indirectSale ? recipientId : prescripteurId;
                const contact = await searchContacts('', [tiersAContacter], true);
                if (!contact || contact.length === 0) {
                    enqueueSnackbar('Aucun contact trouvé pour le client sélectionné', {
                        variant: 'error',
                    });
                    return;
                }
                if (type === 'facture' && collections !== undefined) {
                    const builtAttachments = await Promise.all(
                        invoiceToPayItems.map(async (id) => {
                            const blob = await downloadInvoice(id);
                            const invoice = invoices.find((inv) => inv.id === id);
                            const filename = invoice
                                ? `${getInvoiceLabel(invoice)}.pdf`
                                : `facture_${id}.pdf`;
                            return {
                                file: new File([blob], filename, { type: 'application/pdf' }),
                            };
                        }),
                    );

                    const body: string = await bodyMailFacture(
                        `${contact[0].firstName} ${contact[0].lastName}`,
                        collections[0], // todo llc 1876 ! pas [0]
                        dataPrefacturation?.resteAPayer || '0',
                        invoiceToPayItems,
                    );
                    showMailModal({
                        to: [
                            {
                                emailAddress: contact[0].emailAddress,
                                firstName: contact[0].firstName,
                                lastName: contact[0].lastName,
                            },
                        ],
                        subject: `Demande de règlement`,
                        body,
                        showTo: true,
                        searchParamsTiersIds: [prescripteurId as number, recipientId],
                        showBody: true,
                        showAttachments: true,
                        attachments: [...builtAttachments],
                    });
                } else if (type === 'collection' && collections !== undefined) {
                    if (!prescriber) {
                        enqueueSnackbar('Prescripteur non trouvé', {
                            variant: 'error',
                        });
                    } else {
                        const body: string = await bodyMailCollection(
                            `${contact[0].firstName} ${contact[0].lastName}`,
                            collections[0], // todo llc 1876 ! pas [0]
                        );

                        showMailModal({
                            to: [
                                {
                                    emailAddress: contact[0].emailAddress,
                                    firstName: contact[0].firstName,
                                    lastName: contact[0].lastName,
                                },
                            ],
                            subject: `Demande de provision`,
                            body,
                            showTo: true,
                            searchParamsTiersIds: [prescripteurId as number, recipientId],
                            showBody: true,
                            showAttachments: true,
                        });
                    }
                } else {
                    console.log('Pas de donnée à mettre dans le panier');
                    enqueueSnackbar('Une erreur est survenue', {
                        variant: 'error',
                    });
                }
            } catch (error) {
                console.log(error);
                enqueueSnackbar('Une erreur est survenue', {
                    variant: 'error',
                });
            }
        },
        [
            recipientId,
            clientId,
            prescripteurId,
            collections,
            indirectSale,
            searchContacts,
            enqueueSnackbar,
            invoiceToPayItems,
            bodyMailFacture,
            dataPrefacturation?.resteAPayer,
            showMailModal,
            prescriber,
            downloadInvoice,
            invoices,
            getInvoiceLabel,
            bodyMailCollection,
        ],
    );

    const onClickPaiement = useCallback(
        (amount: number, ids: Array<number>, type: 'collection' | 'facture') => {
            modalActions.call(
                <ModalPaiement
                    ids={ids}
                    amount={amount}
                    type={type}
                    dossierNumero={dossier?.numero as string}
                    chequePayActif={false}
                    virementPayActif={false}
                    typoBackground={false}
                />,
            );
        },
        [dossier?.numero, modalActions],
    );

    const handleHideInactivesQuotes = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setActif(checked);
        },
        [],
    );

    const isLoading = !(allDataLoaded && prescriber && collections);

    return (
        <SkeletonLoader
            isLoading={isLoading}
            skeleton={
                <ListCardBone
                    itemCount={3}
                    contentHeight="220px"
                    gap="16px"
                />
            }
        >
            {collections &&
            recipientId &&
            prescriber &&
            prescriber.id &&
            indirectSale !== undefined ? (
                <BillingAddressSection
                    prescriberId={prescriber.id}
                    recipientId={recipientId}
                    prescriberLabel={prescriber.raisonSociale || ' - '}
                    prescriberSiren={prescriber.siren || ' - '}
                    prescriberAddress={getBillingAddress(prescriber)}
                    handleRecipientChange={handleRecipientChange}
                    clientAddresses={clientAddresses}
                    billingType={indirectSale}
                    handleBillingTypeChange={handleIndirectSaleChange}
                    canEdit={canUpdateBillingInfos(invoices)}
                    isLoading={!dossier}
                />
            ) : null}

            <QuoteAndBillsSection
                actif={actif}
                handleHideInactivesQuotes={handleHideInactivesQuotes}
                devisFactures={devisFactures}
                invoices={invoices}
                totalRemainingAmount={totalRemainingAmount}
                invoiceToPayItems={invoiceToPayItems}
                onClickDemandeReglement={onClickDemandeReglement}
                onClickPaiement={onClickPaiement}
            />

            {collections ? (
                <DossierFacturation
                    reloadInvoiceEstimates={reloadInvoiceEstimates}
                    dataPrefacturation={dataPrefacturation}
                    acomptes={acomptes}
                    reglements={reglements}
                    collections={collections}
                    sortableListOnChange={sortableListOnChange}
                    ajustInvoice={ajustInvoice}
                    afterSubmit={afterSubmit}
                    afterSubmitAcompte={afterSubmitAcompte}
                    afterSubmitInvoice={afterSubmitInvoice}
                    afterSubmitAcompteAttendu={afterSubmitAcompteAttendu}
                    onClickDemandeReglement={onClickDemandeReglement}
                    onClickPaiement={onClickPaiement}
                    handleValidateInvoice={handleValidateInvoice}
                    onload={onload}
                    dossiersSelected={dossiersSelected}
                    onChangeDossiersSelected={(id, checked) => {
                        if (checked) {
                            setDossiersSelected((prev) => [...prev, id]);
                        } else {
                            setDossiersSelected((prev) => prev.filter((i) => i !== id));
                        }
                    }}
                    activeInvoice={
                        invoices.filter(
                            (item) =>
                                item.totalTtc &&
                                !item.factureAvoir &&
                                item.deleted === false &&
                                !item.idFactureRefAvoir,
                        ).length
                    }
                />
            ) : null}
        </SkeletonLoader>
    );
};

export default FacturationTab;
